import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Reveal, MaskedLines } from './motion/Reveal';
import { TID } from '@/constants/testIds';

const STORY_SOAP =
  'https://customer-assets.emergentagent.com/job_choco-wash-landing/artifacts/8mhwhyxj_ChatGPT%20Image%20Jun%2029%2C%202026%2C%2001_39_35%20PM.png';
const STORY_CHOC =
  'https://customer-assets.emergentagent.com/job_choco-wash-landing/artifacts/b6varrok_ChatGPT%20Image%20Jun%2029%2C%202026%2C%2002_25_21%20PM.png';

const chapters = [
  {
    n: '01',
    title: 'Two pots. One stove.',
    body:
      'CHOCO-WASH began in a quiet by-lane of Nagpur with two stainless steel pots on the same stove — one melting cocoa, the other curing soap. We liked how the rituals mirrored each other: a small daily kindness, a slow, intentional act.',
  },
  {
    n: '02',
    title: 'Slow, on purpose.',
    body:
      'Every bar is hand-poured. Every chocolate is conched for the time it takes. No fillers, no shortcuts, no maximalism — twenty-something carefully chosen ingredients and a great deal of patience.',
  },
  {
    n: '03',
    title: 'Made in Nagpur. Sent everywhere.',
    body:
      'From our sunlit studio in Nagpur to a doorstep near you — pan-India. Wrapped in linen, ribbon, and a note from us. If the packaging looks a little imperfect, that means it was folded by hand.',
  },
];

export const StorySection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const yA = useTransform(scrollYProgress, [0, 1], [-40, 40]);
  const yB = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const rot = useTransform(scrollYProgress, [0, 1], [-4, 4]);

  return (
    <section
      id="story"
      ref={ref}
      data-testid={TID.aboutSection}
      className="cw-section relative overflow-hidden"
    >
      <div className="cw-container">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          {/* Left rail — editorial section marker */}
          <div className="md:col-span-3">
            <Reveal>
              <div className="cw-label">04 — The Manifesto</div>
              <div className="mt-6 font-heading text-[10rem] leading-none font-medium text-[var(--cw-text)]/10 select-none">
                &#8203;§
              </div>
            </Reveal>
          </div>

          {/* Right column — headline + chapters */}
          <div className="md:col-span-9">
            <MaskedLines
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05]"
              lines={[
                <>A kitchen experiment</>,
                <>
                  that <span className="italic font-light">refused to end</span>.
                </>,
              ]}
            />

            <div className="mt-14 space-y-14">
              {chapters.map((c, i) => (
                <Reveal key={c.n} delay={i * 0.06}>
                  <article className="grid grid-cols-12 gap-4 md:gap-8 border-t border-[var(--cw-border)] pt-8">
                    <div className="col-span-3 md:col-span-2">
                      <div className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-[var(--cw-blue-hover)]">
                        {c.n}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--cw-muted)] mt-2">
                        Chapter
                      </div>
                    </div>
                    <div className="col-span-9 md:col-span-10">
                      <h3 className="font-heading text-2xl md:text-3xl font-medium tracking-tight leading-tight">
                        {c.title}
                      </h3>
                      <p className="mt-3 text-[var(--cw-muted)] leading-relaxed max-w-2xl">
                        {c.body}
                      </p>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>

            {/* Editorial dual-image band */}
            <Reveal delay={0.1} className="mt-16">
              <div className="relative grid grid-cols-2 gap-4 sm:gap-6">
                <motion.div
                  style={{ y: yA, rotate: rot }}
                  className="relative rounded-[1.5rem] overflow-hidden cw-soft-shadow border-4 border-white bg-[#FCEEF0] aspect-[4/5]"
                >
                  <img
                    src={STORY_SOAP}
                    alt="Rose milk marble soap"
                    className="w-full h-full object-cover"
                  />
                  <span className="absolute bottom-3 left-3 text-[10px] uppercase tracking-[0.22em] font-semibold bg-white/85 backdrop-blur-sm rounded-full px-3 py-1">
                    Soap
                  </span>
                </motion.div>
                <motion.div
                  style={{ y: yB, rotate: rot }}
                  className="relative rounded-[1.5rem] overflow-hidden cw-soft-shadow border-4 border-white bg-[#F5EFE2] aspect-[4/5] mt-16"
                >
                  <img
                    src={STORY_CHOC}
                    alt="Dark chocolate bars"
                    className="w-full h-full object-cover"
                  />
                  <span className="absolute bottom-3 left-3 text-[10px] uppercase tracking-[0.22em] font-semibold bg-white/85 backdrop-blur-sm rounded-full px-3 py-1">
                    Chocolate
                  </span>
                </motion.div>

                {/* Ampersand token between */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-[var(--cw-yellow)] flex items-center justify-center font-heading text-lg font-medium shadow-md">
                  &amp;
                </div>
              </div>
            </Reveal>

            {/* Numbered stats */}
            <Reveal delay={0.1} className="mt-16 grid grid-cols-3 gap-6">
              {[
                { k: '5', l: 'Soap bars' },
                { k: '5', l: 'Chocolate bars' },
                { k: '2', l: 'Gift hampers' },
              ].map((s) => (
                <div
                  key={s.l}
                  className="bg-white/70 border border-[var(--cw-border)] rounded-2xl p-5"
                >
                  <div className="font-heading text-4xl font-medium">{s.k}</div>
                  <div className="text-xs uppercase tracking-[0.18em] text-[var(--cw-muted)] mt-2">
                    {s.l}
                  </div>
                </div>
              ))}
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
